"use client";

import React, { createContext, useContext, useState } from "react";
import localFont from "next/font/local";
import { cn } from "@shared-utils/ui";
import { themeConfig, type ThemeMode } from "@shared-utils/theme-config";

type ThemeContextType = {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
};

const ibmPlexSans = localFont({
  src: [
    {
      path: "../../assets/font/IBMPlexSans-Regular.woff2",
      weight: "400",
      style: "normal",
    },
    {
      path: "../../assets/font/IBMPlexSans-Medium.woff2",
      weight: "500",
      style: "normal",
    },
    {
      path: "../../assets/font/IBMPlexSans-Bold.woff2",
      weight: "700",
      style: "normal",
    },
  ],
  variable: "--font-ibm-plex-sans",
  display: "swap",
  // Load only the weights used by the current route, rather than preloading all three.
  preload: false,
});

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = (
  props: React.PropsWithChildren & {
    themeMode: ThemeMode;
  },
) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(props.themeMode);

  const toggleThemeMode = () => {
    const newThemeMode = themeMode === "dark" ? "light" : "dark";
    setThemeMode(newThemeMode);

    document.cookie = `${themeConfig.cookieKey}=${newThemeMode}; path=/; max-age=${1000 * 60 * 60 * 24 * 365 * 5}`; // 5 year - note that this will be capped by most browser to around 1 year
  };

  return (
    <ThemeContext.Provider value={{ themeMode, toggleThemeMode }}>
      <html className={cn(ibmPlexSans.variable, themeMode)}>
        {props.children}
      </html>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
